import classNames from "classnames";
import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { PWSSessionBackend, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import Spinner from "../../../../../core/ui/Spinner.component";
import { axiosErrorReason } from "../../../lib/coastCommon";
import { emailPattern, FORM_ERRORS, SimpleInput } from "../../forms";
import { RecaptchaForm, RecaptchaFromOnSubmit } from "../../forms/RecaptchaForm.component";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import type { Store } from "@inferno/renderer-shared-core";
import { CoastVerifyEmailSuccess } from "./CoastVerifyEmailSuccess.component";

interface CoastVerifyEmailProps {
  session: PWSSessionBackend;
  goForward: () => void;
  onCancel: () => void;
  goBack: () => void;
  stepNumber: number;
  stepCount: number;
  hidden: boolean;
  store?: Store;
}

export interface EmailFormInputs {
  email: string;
}

export const CoastVerifyEmail = inject("store")(
  ({ onCancel, hidden, goBack, session, stepNumber, stepCount, store }: CoastVerifyEmailProps) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [formError, setFormError] = useState<string>();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [email, setEmail] = useState("");

    if (!store) {
      return null;
    }

    const emailFormMethods = useForm<EmailFormInputs>();
    const { errors, register } = emailFormMethods;

    const preSubmit = useCallback(() => {
      if (formError) {
        setFormError("");
      }
      setIsProcessing(true);
    }, [formError]);

    const onSubmit: RecaptchaFromOnSubmit<EmailFormInputs> = useCallback(
      async ({ email: emailInput }, token) => {
        setEmail(emailInput);
        try {
          await session.memberService.verifyEmail(emailInput, token);
          setIsSubmitted(true);
        } catch (e) {
          setFormError(axiosErrorReason(e) || COAST_MESSAGES.UNEXPECTED_ERROR);
        } finally {
          setIsProcessing(false);
        }
      },
      [session.memberService],
    );
    if (isSubmitted) {
      return <CoastVerifyEmailSuccess email={email} />;
    }

    return (
      <section className={classNames("component-pick-your-plan", "sign-up-step", { hidden })}>
        <p className="step-indicator">
          Step {stepNumber} of {stepCount}
        </p>

        <Heading level={2} className="section-title">
          Create Your Account
        </Heading>
        <FormProvider {...emailFormMethods}>
          <RecaptchaForm
            siteKey={store.env.COAST_RECAPTCHA_V3_PUBLIC_KEY}
            setIsProcessing={setIsProcessing}
            action="signup"
            setError={setFormError}
            preSubmit={preSubmit}
            onSubmit={onSubmit}
          >
            <SimpleInput
              placeholder="Enter your email address"
              rules={{
                required: FORM_ERRORS.required,
                pattern: emailPattern,
              }}
              error={errors.email}
              register={register}
              label="Email"
              name="email"
              type="email"
            />
            <div className="button-prev-next">
              <Button
                disabled={!!errors.email || isProcessing}
                kind={ButtonKind.CUSTOMIZED}
                className="button-continue"
                type="submit"
              >
                {isProcessing ? <Spinner visible={true} /> : "Submit"}
              </Button>
              <Button kind={ButtonKind.CUSTOMIZED} className="button-previous" type="button" click={goBack}>
                Previous
              </Button>
            </div>
          </RecaptchaForm>
        </FormProvider>

        <CoastAlertDisplayError message={formError} isDisplayed={!!formError} />

        <Button kind={ButtonKind.CUSTOMIZED} className="button-cancel" click={onCancel}>
          Cancel
        </Button>
      </section>
    );
  },
);
